<template>
  <div class="md:p-[50px] lg:p-[100px]">
    <div
      class="bg-white pt-[30px] px-[50px] pb-[50px] rounded-[20px] shadow-md mx-auto space-y-8 lg:w-full lg:h-full flex flex-col gap-[10px]"
    >
      <!-- Title -->
      <div class="text-center space-y-4 flex flex-col items-center">
        <h2
          class="text-[24px] md:text-[32px] lg:text-[36px] font-semibold text-gray-800"
        >
          {{ getLocalized1("title") }}
        </h2>
        <p
          class="text-[16px] lg:text-[24px] text-gray-500 lg:w-[500px] lg:h-[80px] text-center mx-auto"
        >
          {{ getLocalized1("paragraph") }}
        </p>
      </div>

      <!-- Form -->
      <form class="space-y-6">
        <!-- Name and Email Row -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Name Field -->
          <div>
            <label class="block text-gray-700 font-medium mb-2" for="name">{{
              getLocalized1("name")
            }}</label>
            <input
              required
              v-model="name"
              id="name"
              type="text"
              :placeholder="getLocalized1('name')"
              class="w-full p-3 border rounded-lg bg-gray-100 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <!-- Email Field -->
          <div>
            <label class="block text-gray-700 font-medium mb-2" for="email"
              >{{ getLocalized1("email")
              }}<span class="text-red-500 ml-1">*</span></label
            >
            <input
              required
              v-model="email"
              id="email"
              type="email"
              :placeholder="getLocalized1('email')"
              class="w-full p-3 border rounded-lg bg-gray-100 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        <!-- Phone Field -->
        <div>
          <label class="block text-gray-700 font-medium mb-2" for="phone"
            >{{ getLocalized1("phone")
            }}<span class="text-red-500 ml-1">*</span></label
          >
          <input
            required
            v-model="phonenumber"
            id="phone"
            type="tel"
            :placeholder="getLocalized1('phone')"
            class="w-full p-3 border rounded-lg bg-gray-100 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <!-- Message Field -->
        <div>
          <label class="block text-gray-700 font-medium mb-2" for="message">{{
            getLocalized1("message")
          }}</label>
          <textarea
            required
            v-model="message"
            id="message"
            rows="4"
            :placeholder="getLocalized1('message')"
            class="w-full h-[173px] p-3 border rounded-lg bg-gray-100 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none md:rows-10"
          ></textarea>
        </div>

        <!-- Submit Button -->
        <div class="relative flex justify-center md:justify-start text-center">
          <button
            @click.prevent="sendMail"
            class="relative w-[110px] h-[55px] rounded-[60px] px-[30px] py-[16px] bg-tbt-blue text-white flex items-center justify-center"
          >
            <!-- Loading Spinner -->
            <div
              v-if="loading"
              role="status"
              class="absolute inset-0 flex items-center justify-center"
            >
              <svg
                aria-hidden="true"
                class="w-8 h-8 text-gray-200 animate-spin fill-white"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>

            <!-- Button Text -->
            <span v-else>{{ getLocalized1("submit") }}</span>
          </button>
        </div>

        <div
          v-if="alertMessage"
          :class="{
            'text-green-600': alertMessage.includes(
              'Thank you! Your message has been received. We’ll get back to you soon.'
            ),
            'text-red-600': alertMessage.includes('Failed'),
          }"
          class="mt-4"
        >
          <h1>{{ alertMessage }}</h1>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      api: process.env.VUE_APP_API_KEY,
      name: "",
      email: "",
      phonenumber: "",
      message: "",
      alertMessage: "",
      translations: {
        title: {
          en: "Write Message",
          kh: "សរសេរសារមកកាន់យើង",
        },
        paragraph: {
          en: "Share your thought with us now, We would love to help and hearing from you.  ",
          kh: "ចែករំលែកគំនិតរបស់អ្នកជាមួយយើងឥឡូវនេះ",
        },
        name: {
          en: "Name",
          kh: "ឈ្មោះ",
        },
        email: {
          en: "Email",
          kh: "អ៊ីមែល",
        },
        phone: {
          en: "Phone Number ",
          kh: "លេខទូរស័ព្ទ",
        },

        message: {
          en: "Message",
          kh: "សារ",
        },
        submit: {
          en: "Submit",
          kh: "ដាក់ស្នើ",
        },
        yourMsg: {
          en: "Your Message",
          kh: "សរសេរសាររបស់អ្នកនៅទីនេះ...",
        },
      },
      currentLang: localStorage.getItem("language") || "en",
      loading: false,
      sucess: false,
      error: null,
    };
  },
  computed: {
    getLocalized1() {
      return (key) => {
        return this.translations[key]
          ? this.translations[key][this.currentLang]
          : key;
      };
    },
  },
  methods: {
    async sendMail() {
      this.loading = true;
      try {
        const response = await axios.post(this.api + "/api/contacts", {
          name: this.name,
          email: this.email,
          phone_number: this.phonenumber,
          message: this.message,
        });

        if (response.status == 201 || response.status == 200) {
          this.alertMessage =
            "Thank you! Your message has been received. We’ll get back to you soon.";
          this.name = "";
          this.email = "";
          this.phonenumber = "";
          this.message = "";
          setTimeout(() => {
            this.alertMessage = "";
          }, 5000);
          this.sucess = true;
        } else {
          this.alertMessage = "Failed to sent!";
          this.name = "";
          this.email = "";
          this.phonenumber = "";
          this.message = "";
          setTimeout(() => {
            this.alertMessage = "";
          }, 5000);
        }
      } catch (error) {
        console.error(error.message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped></style>
